// AddItemDraftStore — local staged-writes store for items in flight.
//
// Replaces the 30+ useState vars + 12 cascading useEffects in the old
// AddDraftSheet.jsx. Holds ALL identity / placement / sizing axes for
// ONE item being added. No DB writes fire on chip taps; everything
// lives locally until commit() flushes to Supabase in one batched
// burst.
//
// Why a custom store instead of useState scattered across components:
//   - Cascading useEffects fire in unpredictable order (the source of
//     "weights drop off mid-upload" bugs in the old code).
//   - A pure store with explicit setters and an explicit commit
//     boundary makes the data flow auditable. setAxis() runs the
//     autofill cascade synchronously; no race between effects.
//   - useSyncExternalStore lets components subscribe to specific
//     slices (e.g. just `canonicalId`) without re-rendering on every
//     unrelated axis change.
//
// Why not Zustand: would add a dependency for ~50 lines of code we
// can write directly with React's built-in primitives.
//
// Lifecycle:
//   1. createDraftStore() at sheet open — fresh store per item
//   2. store.setPipeline("manual" | "wizard" | "photo" | "scan")
//   3. user interactions → setAxis() / setCanonical() / etc.
//   4. commit() at Place CTA → flushes pendingWrites
//   5. caller drops the store reference; GC reclaims
//
// Shop Mode: instantiates N stores in parallel (one per trip_scan in
// flight). Place-all calls Promise.all(stores.map(s => s.commit()))
// for batched flush.

import { useSyncExternalStore } from "react";

// Initial state — every axis null/empty so the autofill cascade
// has a clean slate to work from.
function makeInitialState() {
  return {
    pipeline: null,
    step: null,
    barcodeUpc: null,

    // Identity axes
    canonicalId: null,
    canonicalName: null,
    canonicalEmoji: null,
    brand: null,
    // variant — flavor / product-line qualifier under brand. e.g.
    // brand="Pepsi", variant="Wild Cherry"; brand="Cheerios",
    // variant="Honey Nut". The canonical stays generic ("soda" /
    // "cereal") — variants live as a structured axis below brand
    // so brand_nutrition can key on the exact (canonical, brand,
    // variant) tuple. See resolveBrandAndVariant.js.
    variant: null,
    state: null,
    cut: null,

    // Placement axes
    category: null,
    location: null,
    tileId: null,
    typeId: null,
    typeLabel: null,

    // Sizing
    packageAmount: null,
    packageUnit: null,
    remaining: 1,

    // Override flags — once a user explicitly sets an axis, the
    // autofill cascade respects it. Without this, picking a canonical
    // would clobber a user's manually-corrected location.
    overridden: new Set(),

    // Source attribution for the FOUND-vs-MISSING UI in ScanReview.
    // Each axis records where its current value came from:
    //   "user" — explicit user pick
    //   "correction" — pulled from manual_corrections / barcode_identity_corrections
    //   "off" — OFF API payload
    //   "usda" — USDA FDC payload
    //   "infer" — text-inference fallback
    //   "default" — hard default (e.g., defaultLocationForCategory)
    sources: {},

    // Deferred writes flushed at commit() time. Built incrementally
    // as the user fills axes; the commit() flush is a single
    // Promise.all so all DB hits land in one round-trip.
    pendingWrites: {
      pantryRow: null,
      correctionWrite: null,
      brandNutrition: null,
      popularPackageSeed: null,
    },

    // Misc context the pipelines may set
    expiresAt: "auto",
    name: "",          // pinned to canonical's display name when bound
    nutrition: null,   // from OFF/USDA/photo (carries through to brand_nutrition)
    ingredientsText: null,
    claims: null,
    barcodeMetadata: null,
  };
}

// createDraftStore — factory. Each call returns a fresh, isolated
// store. Stores expose:
//   - getState()       — synchronous snapshot
//   - subscribe(fn)    — listen for changes; returns unsubscribe
//   - setAxis(k, v, source) — generic setter; recomputes sources map
//   - setCanonical(id, source) — special: triggers autofill for downstream
//   - resetOverrides() — clear override flags (rarely needed)
//   - reset()          — back to initial state
//   - commit()         — async flush of pendingWrites
//   - buildPantryRow() — pure; returns the pantry_items insert payload
export function createDraftStore() {
  let state = makeInitialState();
  const listeners = new Set();

  function emit() {
    for (const fn of listeners) fn();
  }

  function setState(updater) {
    const next = typeof updater === "function" ? updater(state) : updater;
    state = next;
    emit();
  }

  function setAxis(key, value, source = "user") {
    setState((s) => {
      const next = { ...s, [key]: value };
      // User picks always mark as overridden so subsequent autofill
      // calls leave the value alone. Non-user sources (autofill,
      // correction) only set the value if the axis isn't already
      // overridden — that gate lives in the caller (canonicalAutofill).
      const overridden = new Set(s.overridden);
      if (source === "user") overridden.add(key);
      next.overridden = overridden;
      next.sources = { ...s.sources, [key]: source };
      return next;
    });
  }

  // setCanonical is the trigger for the full autofill cascade. The
  // store doesn't import canonicalAutofill directly (would create a
  // circular dep). Instead, the calling pipeline imports the autofill
  // helper, calls it with the chosen canonical, and then calls
  // applyAutofill(result) to merge non-overridden defaults.
  function setCanonical(canonicalId, name, emoji, source = "user") {
    setState((s) => {
      const overridden = new Set(s.overridden);
      if (source === "user") overridden.add("canonicalId");
      // Per CLAUDE.md identity-hierarchy: when a canonical resolves,
      // the `name` form field UNCONDITIONALLY pins to the canonical's
      // display name — even if the user typed something earlier, even
      // if OFF returned a long marketing productName, even if a prior
      // override flag is set. The lock only releases later when the
      // user types text that diverges from the canonical's name.
      // Releasing the prior `name` override is part of pinning so the
      // next setCanonical call doesn't get short-circuited by stale state.
      if (name) overridden.delete("name");
      return {
        ...s,
        canonicalId,
        canonicalName: name || s.canonicalName,
        canonicalEmoji: emoji || s.canonicalEmoji,
        name: name || s.name,
        overridden,
        sources: {
          ...s.sources,
          canonicalId: source,
          ...(name ? { name: source } : null),
        },
      };
    });
  }

  // applyAutofill merges autofill results — only fills axes not
  // already overridden by user. Each value carries an explicit source.
  function applyAutofill(autofillResult) {
    if (!autofillResult || typeof autofillResult !== "object") return;
    setState((s) => {
      const next = { ...s };
      const sources = { ...s.sources };
      const fields = [
        "category", "location", "tileId", "typeId", "typeLabel",
        "state", "cut", "packageAmount", "packageUnit",
      ];
      for (const f of fields) {
        if (s.overridden.has(f)) continue;
        if (autofillResult[f] == null) continue;
        next[f] = autofillResult[f];
        sources[f] = autofillResult.source || "autofill";
      }
      next.sources = sources;
      return next;
    });
  }

  function resetOverrides() {
    setState((s) => ({ ...s, overridden: new Set() }));
  }

  function reset() {
    setState(makeInitialState());
  }

  // seedFromRow — hydrate from an existing pantry_items row for the
  // edit flow. Marks every populated axis as overridden so the
  // canonical-autofill cascade can't clobber the user's prior values
  // when they tap a chip later. Source attribution flips to "user"
  // since these values are authoritative — they came from a
  // committed row, not a fresh inference.
  function seedFromRow(row) {
    if (!row) return;
    setState((s) => {
      const next = { ...s };
      const sources = { ...s.sources };
      const overridden = new Set(s.overridden);
      const seed = (key, value, fromCol = key) => {
        if (value == null || value === "") return;
        next[key] = value;
        sources[key] = "user";
        overridden.add(key);
      };
      // Identity
      seed("canonicalId",   row.canonical_id ?? row.canonicalId);
      seed("brand",         row.brand);
      seed("variant",       row.variant ?? row.attributes?.variant);
      seed("state",         row.state);
      seed("cut",           row.cut);
      // Placement
      seed("category",      row.category);
      seed("location",      row.location);
      seed("tileId",        row.tile_id ?? row.tileId);
      seed("typeId",        row.type_id ?? row.typeId);
      // Sizing — `max` is the package size as sold; `amount` is
      // current remaining; we derive `remaining` (0..1) from the
      // ratio so the gauge round-trips correctly.
      const maxN = Number(row.max);
      if (Number.isFinite(maxN) && maxN > 0) {
        next.packageAmount = maxN;
        sources.packageAmount = "user";
        overridden.add("packageAmount");
      }
      seed("packageUnit",   row.unit);
      const amtN = Number(row.amount);
      if (Number.isFinite(maxN) && maxN > 0 && Number.isFinite(amtN)) {
        next.remaining = Math.max(0, Math.min(1, amtN / maxN));
      }
      // Identity helpers + meta
      next.name           = row.name || s.name;
      next.canonicalName  = row.canonicalName ?? row.canonical_name ?? s.canonicalName;
      next.canonicalEmoji = row.emoji || s.canonicalEmoji;
      next.barcodeUpc     = row.barcode_upc ?? row.barcodeUpc ?? s.barcodeUpc;
      next.expiresAt      = row.expires_at ?? row.expiresAt ?? s.expiresAt;
      next.claims         = row.attributes?.claims ?? s.claims;

      next.sources    = sources;
      next.overridden = overridden;
      return next;
    });
  }

  // buildPantryRow — pure projection of state into the
  // pantry_items insert payload. Doesn't write; just returns the
  // shape. Caller (commit()) feeds this to supabase.insert.
  function buildPantryRow() {
    const s = state;
    const pkgN = Number(s.packageAmount);
    const maxN = Number.isFinite(pkgN) && pkgN > 0 ? pkgN : null;
    const remN = Math.max(0, Math.min(1, Number(s.remaining) || 1));
    const amtN = maxN != null ? remN * maxN : null;
    // CamelCase ALL fields. The row is consumed by setPantry which
    // holds JS-shape rows (camelCase). useSyncedList's toDb mapper
    // converts those to DB snake_case for the write. Earlier this
    // function emitted snake_case for canonical_id / tile_id /
    // type_id / barcode_upc / expires_at / nutrition_override —
    // which meant setPantry got a row with snake_case keys, toDb
    // looked for camelCase keys and found nothing, and the DB
    // columns persisted as null. The user's gjetost row had brand
    // (already camelCase) but no canonical_id or tile_id. This is
    // why the kitchen view showed gjetost in misc and the title
    // dropped to free-text "Gjetost" instead of buildDisplayName.
    return {
      name: s.name?.trim() || s.canonicalName || null,
      brand: s.brand?.trim() || null,
      amount: amtN,
      max: maxN,
      unit: s.packageUnit?.trim() || null,
      // `category` retired 2026-05-04. The hub chain (canonical →
      // parentId → hub.parentHub → ...) replaces it everywhere:
      // drill-down navigation, brand-picker narrow context, default
      // location, state vocabulary, recipe substitutions. Hubs are
      // recursive (Produce → Fruit → Citrus → Lemon) where category
      // was flat (one level). The column stays on existing rows
      // until the read-site swap is finished and a later migration
      // drops it; new rows simply don't carry it.
      typeId: s.typeId || null,
      canonicalId: s.canonicalId || null,
      state: s.state || null,
      cut: s.cut || null,
      location: s.location || null,
      tileId: s.tileId || null,
      barcodeUpc: s.barcodeUpc || null,
      expiresAt: s.expiresAt instanceof Date ? s.expiresAt : null,
      nutritionOverride: null,
      // attributes carries the unstructured grab-bag axes that don't
      // get their own pantry_items column. variant goes in here so
      // brand stays the parent ("Pepsi") while the row knows it's
      // specifically "Wild Cherry". claims carry adjective tokens
      // ("Premium") + non-variant flavor qualifiers per CLAUDE.md.
      attributes: ((s.claims?.length || s.variant) ? {
        ...(s.claims?.length ? { claims: s.claims } : null),
        ...(s.variant ? { variant: s.variant } : null),
      } : null),
    };
  }

  // commit — flushes pendingWrites in parallel via Promise.all.
  // Returns { ok, errors } — caller decides to close sheet or show
  // banner. The pantry insert is owed by the caller (uses usePantry
  // hooks, can't run here without React context); we return the
  // pantryRow shape and let the caller submit it.
  async function commit({ insertPantryRow, rememberCorrection,
                          upsertBrandNutrition, seedPopularPackage }) {
    const errors = [];
    const ops = [];

    // 1. Pantry row (this is the load-bearing one — fail loudly if it errors)
    const pantryRow = buildPantryRow();
    if (insertPantryRow) {
      ops.push(
        insertPantryRow(pantryRow).catch((e) => {
          errors.push({ op: "pantryRow", error: e?.message || String(e) });
          return null;
        })
      );
    }

    // 2. Correction write (fire-and-forget; errors logged but don't block)
    if (rememberCorrection && state.canonicalId) {
      const key = state.barcodeUpc
        ? { upc: state.barcodeUpc }
        : state.brand
          ? { canonicalAndBrand: { canonical: state.canonicalId, brand: state.brand } }
          : { canonicalOnly: state.canonicalId };
      const axes = {
        canonicalId: state.canonicalId,
        typeId: state.typeId,
        tileId: state.tileId,
        location: state.location,
        state: state.state,
        cut: state.cut,
        brand: state.brand,
        variant: state.variant,
        packageSizeAmount: Number.isFinite(Number(state.packageAmount)) ? Number(state.packageAmount) : null,
        packageSizeUnit: state.packageUnit,
        name: state.name?.trim() || null,
        claims: Array.isArray(state.claims) ? state.claims : null,
      };
      ops.push(
        rememberCorrection({ key, axes }).catch((e) => {
          errors.push({ op: "correction", error: e?.message || String(e) });
        })
      );
    }

    // 3. Brand nutrition (only when nutrition data present)
    if (upsertBrandNutrition && state.canonicalId && state.brand && state.nutrition) {
      ops.push(
        upsertBrandNutrition({
          canonicalId: state.canonicalId,
          brand: state.brand,
          nutrition: state.nutrition,
          barcode: state.barcodeUpc || null,
          source: "additem",
          sourceId: state.barcodeUpc || null,
          confidence: state.barcodeUpc ? 80 : 60,
          ingredientsText: state.ingredientsText || null,
        }).catch((e) => {
          errors.push({ op: "brandNutrition", error: e?.message || String(e) });
        })
      );
    }

    // 4. Popular package seed — only fire if we have a valid (canonical,
    //    brand?, amount, unit) tuple. The RPC handles upsert + counting.
    if (seedPopularPackage && state.canonicalId && state.packageAmount && state.packageUnit) {
      ops.push(
        seedPopularPackage({
          canonicalId: state.canonicalId,
          brand: state.brand || null,
          amount: Number(state.packageAmount),
          unit: state.packageUnit,
        }).catch((e) => {
          errors.push({ op: "popularPackage", error: e?.message || String(e) });
        })
      );
    }

    const results = await Promise.all(ops);
    return {
      ok: errors.length === 0,
      errors,
      pantryRow: results[0] || null,
    };
  }

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    setAxis,
    setCanonical,
    applyAutofill,
    resetOverrides,
    reset,
    seedFromRow,
    buildPantryRow,
    commit,
    // Direct setState for pipeline meta (pipeline / step / barcodeUpc
    // / nutrition / claims) where the source-attribution + override
    // flag aren't relevant. Pipelines call this to record context.
    setMeta(patch) {
      setState((s) => ({ ...s, ...patch }));
    },
    setPendingWrite(key, value) {
      setState((s) => ({
        ...s,
        pendingWrites: { ...s.pendingWrites, [key]: value },
      }));
    },
  };
}

// useDraftStore — selective React hook. Pass a selector to subscribe
// to a specific slice; component re-renders only when that slice
// changes. Same ergonomics as Zustand's `useStore(state => state.foo)`.
//
// Usage:
//   const canonicalId = useDraftStore(store, s => s.canonicalId);
//   const setBrand    = useDraftAction(store, "setAxis");
export function useDraftStore(store, selector) {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
    () => selector(store.getState()),
  );
}

// useDraftAction — returns a stable reference to one of the store's
// action methods. Avoids closing over the store in render bodies.
export function useDraftAction(store, name) {
  return store[name];
}
